import { useState, useRef, useEffect, useMemo } from 'react';
import './ScopeSelector.css';

const ENTITY_TYPE_CLASS = 'class';
const ENTITY_TYPE_STUDENT = 'student';

function studentLabel(student) {
  const first = student.firstName || '';
  const last = student.lastName || '';
  return [first, last].filter(Boolean).join(' ').trim() || 'Unknown';
}

/**
 * Build a single list: students first (default), then classes. Each item { type, id, label }.
 */
function buildCombinedList(classes, students, search) {
  const q = (search || '').toLowerCase().trim();
  const studentItems = students
    .filter((s) => studentLabel(s).toLowerCase().includes(q))
    .map((s) => ({ type: ENTITY_TYPE_STUDENT, id: String(s.id), label: studentLabel(s) }));
  const classItems = (classes || [])
    .filter((c) => (c.label || '').toLowerCase().includes(q))
    .map((c) => ({ type: ENTITY_TYPE_CLASS, id: c.value, label: c.label || c.value }));
  return [...studentItems, ...classItems];
}

function typeChipLabel(type) {
  return type === ENTITY_TYPE_STUDENT ? 'Student' : 'Class';
}

export function ScopeSelector({
  classes = [],
  students = [],
  onSelectEntity,
  onDismiss,
  disabled,
  open: controlledOpen,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };

  const combinedList = useMemo(
    () => buildCombinedList(classes, students, search),
    [classes, students, search]
  );

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setHighlightedIndex(0);
    searchInputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setHighlightedIndex((prev) => Math.min(prev, Math.max(0, combinedList.length - 1)));
  }, [combinedList.length]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onDismiss?.();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onDismiss]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss?.();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onDismiss]);

  const handleSelectItem = (item) => {
    onSelectEntity({ type: item.type, id: item.id, label: item.label });
    setSearch('');
    setOpen(false);
  };

  const handleKeyDownInDropdown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % Math.max(1, combinedList.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev <= 0 ? Math.max(0, combinedList.length - 1) : prev - 1
      );
      return;
    }
    if (e.key === 'Enter') {
      if (combinedList.length > 0 && combinedList[highlightedIndex]) {
        e.preventDefault();
        handleSelectItem(combinedList[highlightedIndex]);
      }
      return;
    }
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el || highlightedIndex < 0) return;
    const child = el.children[highlightedIndex];
    if (child) child.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlightedIndex]);

  return (
    <div className="scope-selector-toolbar" ref={containerRef}>
      <button
        type="button"
        className="scope-selector-trigger"
        onClick={() => setOpen(!open)}
        title="Add a class or student (or type @ in the message)"
        disabled={disabled}
      >
        <span className="scope-selector-trigger-icon">⊕</span>
        <span>Choose who to include
            
        </span>
      </button>

      {open && (
        <div className="scope-selector-dropdown" onKeyDown={handleKeyDownInDropdown}>
          <input
            ref={searchInputRef}
            type="text"
            className="scope-selector-search"
            placeholder="Search classes and students…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          <div className="scope-selector-list" ref={listRef}>
            {combinedList.length === 0 ? (
              <div className="scope-selector-empty">No classes or students match</div>
            ) : (
              combinedList.map((item, i) => (
                <button
                  key={`${item.type}-${item.id}-${i}`}
                  type="button"
                  className={`scope-selector-item ${i === highlightedIndex ? 'scope-selector-item-highlighted' : ''}`}
                  onClick={() => handleSelectItem(item)}
                  onMouseEnter={() => setHighlightedIndex(i)}
                >
                  <span className={`scope-selector-item-type-chip scope-selector-item-type-chip--${item.type}`}>
                    {typeChipLabel(item.type)}
                  </span>
                  <span className="scope-selector-item-label">{item.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
